import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, FormControlLabel, Switch, Alert, MenuItem } from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import groupsService from '../../services/groups.service';
import { useLanguage } from '../../i18n/LanguageContext';
import DimensionSelector, { DimensionSelectorRef } from '../../components/DimensionSelector';

const SupplierForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isEdit = Boolean(id);
  const [error, setError] = useState('');
  const [supplierGroups, setSupplierGroups] = useState<any[]>([]);
  const dimRef = useRef<DimensionSelectorRef>(null);
  const [form, setForm] = useState({
    code: '', name: '', contactPerson: '', email: '', phone: '',
    address: '', city: '', country: '', taxNumber: '', paymentTerms: '',
    supplierGroupId: null as number | null, isActive: true
  });

  useEffect(() => {
    loadGroups();
    if (isEdit) loadSupplier();
  }, []);

  const loadGroups = async () => {
    try { const res = await groupsService.getSupplierGroups(); setSupplierGroups(res.data); } catch { }
  };

  const loadSupplier = async () => {
    try {
      const res = await procurementService.getSupplier(Number(id));
      const s = res.data;
      setForm({
        code: s.code, name: s.name, contactPerson: s.contactPerson || '',
        email: s.email || '', phone: s.phone || '', address: s.address || '',
        city: s.city || '', country: s.country || '', taxNumber: s.taxNumber || '',
        paymentTerms: s.paymentTerms || '', supplierGroupId: s.supplierGroupId || null, isActive: s.isActive
      });
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    try {
      let entityId = id ? Number(id) : 0;
      if (isEdit) await procurementService.updateSupplier(Number(id), form);
      else { const res = await procurementService.createSupplier(form); entityId = res.data.id; }

      try {
        await dimRef.current?.save('SUPPLIER', entityId);
      } catch (dimErr: any) {
        setError(dimErr.response?.data?.details || dimErr.response?.data?.message || 'Error saving dimensions');
        return;
      }

      navigate('/suppliers');
    } catch (err: any) { setError(err.response?.data?.details || err.response?.data?.message || 'Error saving supplier'); }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{isEdit ? t('suppliers.edit') : t('suppliers.new')}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/suppliers')}>{t('common.back_to_list')}</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Typography variant="h6" sx={{ mb: 2 }}>{t('suppliers.basic_info')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth label={t('suppliers.supplier_code')} value={form.code}
          onChange={e => setForm({ ...form, code: e.target.value })}
          placeholder={t('number_sequences.auto_generate')} helperText={!id ? t('number_sequences.auto_generate') : ''} />
        <TextField fullWidth required label={t('suppliers.supplier_name')} value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })} />
        <TextField fullWidth select label={t('suppliers.supplier_group')} value={form.supplierGroupId ?? ''}
          onChange={e => setForm({ ...form, supplierGroupId: e.target.value ? Number(e.target.value) : null })}>
          <MenuItem value="">{t('common.none')}</MenuItem>
          {supplierGroups.map((g: any) => <MenuItem key={g.id} value={g.id}>{g.code} - {g.description}</MenuItem>)}
        </TextField>
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('suppliers.contact_details')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth label={t('common.contact_person')} value={form.contactPerson}
          onChange={e => setForm({ ...form, contactPerson: e.target.value })} />
        <TextField fullWidth label={t('common.email')} type="email" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })} />
        <TextField fullWidth label={t('common.phone')} value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })} />
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('suppliers.address_info')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth label={t('common.address')} value={form.address}
          onChange={e => setForm({ ...form, address: e.target.value })} />
        <TextField fullWidth label={t('common.city')} value={form.city}
          onChange={e => setForm({ ...form, city: e.target.value })} />
        <TextField fullWidth label={t('common.country')} value={form.country}
          onChange={e => setForm({ ...form, country: e.target.value })} />
      </Box>

      <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>{t('suppliers.business_details')}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth label={t('common.tax_number')} value={form.taxNumber}
          onChange={e => setForm({ ...form, taxNumber: e.target.value })} />
        <TextField fullWidth label={t('common.payment_terms')} value={form.paymentTerms}
          onChange={e => setForm({ ...form, paymentTerms: e.target.value })} placeholder="e.g., Net 30, Net 60" />
      </Box>

      <FormControlLabel control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
        label={t('common.active')} />

      <DimensionSelector ref={dimRef} entityType="SUPPLIER" entityId={isEdit ? Number(id) : undefined} />

      <Box sx={{ mt: 3 }}>
        <Button type="submit" variant="contained" startIcon={<Save />} size="large">
          {isEdit ? t('suppliers.update') : t('suppliers.create')}
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierForm;
